import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Row,
  Col,
  Button,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Tooltip,
  Skeleton,
  Upload,
  DatePicker,
  Dropdown,
  Modal,
  Segmented,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  MoreOutlined,
  CalendarOutlined,
  ArrowRightOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  PlusOutlined,
  SearchOutlined,
  CloseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  FolderOpenOutlined,
  UploadOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
  ExportOutlined,
  ProjectOutlined,
  InfoCircleOutlined,
  CameraOutlined,
  UnorderedListOutlined as PlanOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import MainLayout from '../components/MainLayout';
import ProjectPhotosDrawer from '../components/projects/ProjectPhotosDrawer';
import ProjectPlanDrawer from '../components/projects/ProjectPlanDrawer';
import ProjectShareDrawer from '../components/projects/ProjectShareDrawer';
import {
  projectService,
  Project,
  ProjectTask,
  ProjectEmployee,
  ProjectFile,
  Attendance,
  ProjectAttendance,
} from '../services/projectService';
import { employeeService, Employee } from '../services/employeeService';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageHeader, EmptyState, FilterChips, PersonAvatar, Pill, Tone } from '../components/ui';
import { fileKind } from '../components/ui/fileKind';
import ProjectAlerts from '../components/projects/ProjectAlerts';
import {
  getSchedule,
  isAlert,
  scheduleLabel,
  formatScheduleDate,
  Schedule,
  ScheduleState,
  SCHEDULE_TONE,
  SOON_DAYS,
} from '../components/projects/projectSchedule';
import './Projects.css';

const { TextArea } = Input;

type StatusKey = 'active' | 'finished' | 'canceled';
type StatusFilter = 'all' | 'attention' | StatusKey;
type SortKey = 'schedule' | 'newest' | 'name';
type ViewKey = 'grid' | 'list';

const STATUS_ORDER: StatusKey[] = ['active', 'finished', 'canceled'];
const STATUS_TONE: Record<StatusKey, Tone> = { active: 'success', finished: 'primary', canceled: 'danger' };

// "By schedule": what needs attention first, then what's running, then what's still ahead
const SCHEDULE_ORDER: ScheduleState[] = ['overdue', 'endsSoon', 'startsSoon', 'running', 'upcoming', 'unscheduled', 'closed'];
/** Days to the date that matters for this state, so the most urgent comes first within it. */
const urgency = (schedule: Schedule) =>
  schedule.state === 'startsSoon' || schedule.state === 'upcoming'
    ? schedule.daysToStart ?? 0
    : schedule.daysToEnd ?? Number.MAX_SAFE_INTEGER;

const VIEW_STORAGE_KEY = 'e22.projects.view';
const readStoredView = (): ViewKey => {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
};

const Projects: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('schedule');
  const [view, setView] = useState<ViewKey>(readStoredView);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const highlightTimer = useRef<number | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const [modal, modalContextHolder] = Modal.useModal();

  // Project form
  const [formVisible, setFormVisible] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // Drawers keyed by project id, so they follow the list after every refetch
  const [teamProjectId, setTeamProjectId] = useState<number | null>(null);
  const [filesProjectId, setFilesProjectId] = useState<number | null>(null);
  const [photosProjectId, setPhotosProjectId] = useState<number | null>(null);
  const [shareProjectId, setShareProjectId] = useState<number | null>(null);
  const [assigningEmployeeId, setAssigningEmployeeId] = useState<number | undefined>();
  const [assigning, setAssigning] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<number | null>(null);

  // Attendance
  const [attendanceProject, setAttendanceProject] = useState<Project | null>(null);
  const [attendanceData, setAttendanceData] = useState<ProjectAttendance | null>(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setProjects(await projectService.getProjects());
    } catch (error) {
      toast.error(t('projects.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchProjects();
    employeeService
      .getAll()
      .then(setAllEmployees)
      .catch(() => toast.error(t('employees.fetchError')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teamProject = projects.find((project) => project.id === teamProjectId) ?? null;
  const filesProject = projects.find((project) => project.id === filesProjectId) ?? null;

  // ---------- Project ----------
  const openForm = (project?: Project) => {
    setEditingProject(project ?? null);
    form.resetFields();
    if (project)
      form.setFieldsValue({
        ...project,
        start_date: project.start_date ? dayjs(project.start_date) : null,
        end_date: project.end_date ? dayjs(project.end_date) : null,
      });
    setFormVisible(true);
  };

  const handleSubmit = async (formValues: any) => {
    // Pickers hold dayjs values; the API wants YYYY-MM-DD, and null clears a date
    const values = {
      ...formValues,
      start_date: formValues.start_date ? (formValues.start_date as Dayjs).format('YYYY-MM-DD') : null,
      end_date: formValues.end_date ? (formValues.end_date as Dayjs).format('YYYY-MM-DD') : null,
    };
    try {
      setSaving(true);
      if (editingProject) {
        await projectService.updateProject(editingProject.id, values);
        toast.success(t('projects.updateSuccess'));
      } else {
        await projectService.createProject(values);
        toast.success(t('projects.createSuccess'));
      }
      setFormVisible(false);
      fetchProjects();
    } catch (error: any) {
      if (error.response?.data?.errors) {
        Object.values(error.response.data.errors as Record<string, string[]>).forEach((messages) =>
          toast.error(messages[0]),
        );
      } else {
        toast.error(error.response?.data?.message || t('projects.createError'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await projectService.deleteProject(id);
      toast.success(t('projects.deleteSuccess'));
      fetchProjects();
    } catch (error) {
      // A project that still has files cannot be deleted (foreign key), which is worth saying
      toast.error(t('projects.deleteError'));
    }
  };

  const confirmDelete = (project: Project) =>
    modal.confirm({
      title: t('projects.deleteConfirm'),
      content: project.name,
      okText: t('common.yes'),
      cancelText: t('common.no'),
      okButtonProps: { danger: true },
      onOk: () => handleDelete(project.id),
    });

  // ---------- Plan (tasks board) ----------
  // Kept in the URL (?plan=<id>) so a refresh or the back button behaves
  const planProjectId = Number(searchParams.get('plan')) || null;
  const planProject = projects.find((project) => project.id === planProjectId) ?? null;
  const openPlan = (project: Project) => setSearchParams({ plan: String(project.id) });
  const closePlan = () => setSearchParams({}, { replace: true });

  /** Keeps the card's plan progress in step with the board without refetching the list. */
  const handleTasksChange = useCallback((projectId: number, tasks: ProjectTask[]) => {
    const total = tasks.reduce((sum, task) => sum + task.percentage, 0);
    const done = tasks.filter((task) => task.status === 'done').reduce((sum, task) => sum + task.percentage, 0);
    setProjects((current) =>
      current.map((project) =>
        project.id === projectId
          ? { ...project, tasks_count: tasks.length, tasks_total_percentage: total, tasks_done_percentage: done }
          : project,
      ),
    );
  }, []);

  const actionItems = (project: Project): MenuProps['items'] => [
    { key: 'plan', icon: <PlanOutlined />, label: t('projects.plan.open'), onClick: () => openPlan(project) },
    { key: 'edit', icon: <EditOutlined />, label: t('projects.actions.editProject'), onClick: () => openForm(project) },
    { key: 'team', icon: <TeamOutlined />, label: t('projects.actions.manageEmployees'), onClick: () => setTeamProjectId(project.id) },
    { key: 'files', icon: <FolderOpenOutlined />, label: t('projects.files.title'), onClick: () => openFiles(project) },
    { key: 'hours', icon: <ClockCircleOutlined />, label: t('projects.attendance.title'), onClick: () => openAttendance(project) },
    { key: 'photos', icon: <CameraOutlined />, label: t('projects.photos.button'), onClick: () => setPhotosProjectId(project.id) },
    { key: 'share', icon: <ShareAltOutlined />, label: t('projects.share.menu'), onClick: () => setShareProjectId(project.id) },
    { type: 'divider' },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: t('projects.actions.deleteProject'),
      danger: true,
      onClick: () => confirmDelete(project),
    },
  ];

  // ---------- Team ----------
  const handleAssignEmployee = async (employeeId?: number) => {
    const id = employeeId ?? assigningEmployeeId;
    if (!teamProject || !id) return;
    try {
      setAssigning(true);
      await projectService.assignEmployee(teamProject.id, id);
      toast.success(t('projects.employees.assignSuccess'));
      setAssigningEmployeeId(undefined);
      await fetchProjects();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('projects.employees.assignError'));
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveEmployee = async (employeeId: number) => {
    if (!teamProject) return;
    try {
      await projectService.removeEmployee(teamProject.id, employeeId);
      toast.success(t('projects.employees.removeSuccess'));
      await fetchProjects();
    } catch (error) {
      toast.error(t('projects.employees.removeError'));
    }
  };

  // ---------- Files ----------
  const openFiles = (project: Project) => {
    setFilesProjectId(project.id);
    setSelectedFile(null);
    setFileName('');
  };

  const handleFileUpload = async () => {
    if (!filesProject || !selectedFile || !fileName.trim()) return;
    try {
      setUploadingFile(true);
      await projectService.storeFile(filesProject.id, selectedFile, fileName.trim());
      toast.success(t('projects.files.uploadSuccess'));
      setSelectedFile(null);
      setFileName('');
      await fetchProjects();
    } catch (error: any) {
      if (error.response?.data?.errors) {
        Object.values(error.response.data.errors as Record<string, string[]>).forEach((messages) =>
          toast.error(messages[0]),
        );
      } else {
        toast.error(error.response?.data?.message || t('projects.files.uploadError'));
      }
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteFile = async (fileId: number) => {
    try {
      setDeletingFileId(fileId);
      await projectService.deleteFile(fileId);
      toast.success(t('projects.files.deleteSuccess'));
      await fetchProjects();
    } catch (error) {
      toast.error(t('projects.files.deleteError'));
    } finally {
      setDeletingFileId(null);
    }
  };

  // ---------- Attendance ----------
  const openAttendance = async (project: Project) => {
    setAttendanceProject(project);
    setAttendanceData(null);
    try {
      setAttendanceLoading(true);
      setAttendanceData(await projectService.getAttendanceForProject(project.id));
    } catch (error) {
      toast.error(t('projects.attendance.fetchError'));
    } finally {
      setAttendanceLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
  };

  /** Hours per employee, so the drawer answers "who worked how much here" at a glance. */
  const attendanceByEmployee = useMemo(() => {
    const byId = new Map<number, { name: string; seconds: number; records: number }>();
    (attendanceData?.data ?? []).forEach((record) => {
      const key = record.employee?.id ?? record.employee_id;
      const entry = byId.get(key);
      const seconds = record.duration_seconds ?? 0;
      if (entry) {
        entry.seconds += seconds;
        entry.records += 1;
      } else {
        byId.set(key, {
          name: `${record.employee?.name ?? ''} ${record.employee?.surname ?? ''}`.trim() || '—',
          seconds,
          records: 1,
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) => b.seconds - a.seconds);
  }, [attendanceData]);

  // ---------- List ----------
  const normalizedSearch = searchText.trim().toLowerCase();
  const statusCounts = useMemo(() => {
    const counts: Record<StatusKey, number> = { active: 0, finished: 0, canceled: 0 };
    projects.forEach((project) => {
      if (project.status in counts) counts[project.status as StatusKey] += 1;
    });
    return counts;
  }, [projects]);

  const schedules = useMemo(() => {
    const today = dayjs();
    return new Map(projects.map((project) => [project.id, getSchedule(project, today)]));
  }, [projects]);
  const scheduleOf = (project: Project) => schedules.get(project.id) ?? getSchedule(project);
  const attentionCount = useMemo(
    () => Array.from(schedules.values()).filter(isAlert).length,
    [schedules],
  );

  const visibleProjects = useMemo(() => {
    const byStatus = (a: Project, b: Project) =>
      STATUS_ORDER.indexOf(a.status as StatusKey) - STATUS_ORDER.indexOf(b.status as StatusKey);
    const list = projects.filter((project) => {
      if (normalizedSearch && !`${project.name} ${project.location}`.toLowerCase().includes(normalizedSearch)) return false;
      if (statusFilter === 'attention') return isAlert(schedules.get(project.id)!);
      return statusFilter === 'all' || project.status === statusFilter;
    });
    // Array.sort is stable, so the API's newest-first order survives as the tie-breaker
    if (sortKey === 'name') return list.sort((a, b) => a.name.localeCompare(b.name, i18n.language));
    if (sortKey === 'newest') return list.sort(byStatus);
    return list.sort((a, b) => {
      const sa = schedules.get(a.id)!;
      const sb = schedules.get(b.id)!;
      return (
        byStatus(a, b) ||
        SCHEDULE_ORDER.indexOf(sa.state) - SCHEDULE_ORDER.indexOf(sb.state) ||
        (sa.state === 'closed' ? 0 : urgency(sa) - urgency(sb))
      );
    });
  }, [projects, schedules, normalizedSearch, statusFilter, sortKey, i18n.language]);

  const hasFilters = normalizedSearch !== '' || statusFilter !== 'all';
  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
  };

  const changeView = (next: ViewKey) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Remembering the view is only a convenience
    }
  };

  /** Scroll to a project's card and flash it (alerts, and ?project= links from other pages). */
  const focusProject = useCallback(
    (projectId: number) => {
      setSearchText('');
      setStatusFilter('all');
      setHighlightId(projectId);
      window.clearTimeout(highlightTimer.current);
      highlightTimer.current = window.setTimeout(() => setHighlightId(null), 2600);
      // After the filters are cleared and the card is rendered
      window.setTimeout(() => {
        document.getElementById(`project-${projectId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 60);
    },
    [],
  );

  useEffect(() => () => window.clearTimeout(highlightTimer.current), []);

  // ?project=<id> (from the dashboard / tasks alerts): focus it once the list is in
  const linkedProjectId = Number(searchParams.get('project')) || null;
  useEffect(() => {
    if (loading || !linkedProjectId) return;
    if (projects.some((project) => project.id === linkedProjectId)) focusProject(linkedProjectId);
    setSearchParams({}, { replace: true });
  }, [loading, linkedProjectId, projects, focusProject, setSearchParams]);

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });

  const renderSchedulePill = (schedule: Schedule) =>
    schedule.state === 'closed' || schedule.state === 'unscheduled' ? null : (
      <Pill tone={SCHEDULE_TONE[schedule.state]}>{scheduleLabel(t, schedule)}</Pill>
    );

  /** Start → end with a progress bar; a prompt to plan dates on active projects without any. */
  const renderTimeline = (project: Project, schedule: Schedule, compact = false) => {
    if (!schedule.start && !schedule.end) {
      if (project.status !== 'active') return compact ? <span className="prj-muted">—</span> : null;
      return (
        <button type="button" className="prj-nodates" onClick={() => openForm(project)}>
          <CalendarOutlined aria-hidden />
          <span>{t('projects.schedule.noDates')}</span>
          <span className="prj-nodates-link">{t('projects.schedule.addDates')}</span>
        </button>
      );
    }
    const caption =
      schedule.currentDay && schedule.totalDays && project.status === 'active'
        ? t('projects.schedule.dayOf', { day: schedule.currentDay, total: schedule.totalDays })
        : schedule.totalDays
          ? t('projects.schedule.plannedDays', { count: schedule.totalDays })
          : null;
    const showBar = schedule.progress !== undefined;
    return (
      <div className={`prj-timeline prj-timeline--${schedule.state} ${compact ? 'prj-timeline--compact' : ''}`}>
        <div className="prj-dates">
          <div className="prj-date">
            {!compact && <span className="prj-date-label">{t('projects.startDate')}</span>}
            <span className="prj-date-value">
              {schedule.start ? formatScheduleDate(schedule.start, i18n.language) : t('projects.schedule.notSet')}
            </span>
          </div>
          <ArrowRightOutlined className="prj-date-arrow" aria-hidden />
          <div className="prj-date prj-date--end">
            {!compact && <span className="prj-date-label">{t('projects.endDate')}</span>}
            <span className="prj-date-value">
              {schedule.end ? formatScheduleDate(schedule.end, i18n.language) : t('projects.schedule.notSet')}
            </span>
          </div>
        </div>
        {showBar && (
          <div
            className="prj-bar"
            role="progressbar"
            aria-valuenow={schedule.progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={caption ?? undefined}
          >
            <div className="prj-bar-fill" style={{ width: `${schedule.progress}%` }} />
          </div>
        )}
        {caption && !compact && <div className="prj-timeline-caption">{caption}</div>}
      </div>
    );
  };

  /** Plan progress: share of the project whose tasks are done; a nudge when there's no plan yet. */
  const renderPlan = (project: Project, compact = false) => {
    const count = project.tasks_count ?? 0;
    if (count === 0) {
      if (compact || project.status !== 'active') return null;
      return (
        <button type="button" className="prj-nodates prj-noplan" onClick={() => openPlan(project)}>
          <PlanOutlined aria-hidden />
          <span>{t('projects.plan.noPlan')}</span>
          <span className="prj-nodates-link">{t('projects.plan.createPlan')}</span>
        </button>
      );
    }
    const done = Number(project.tasks_done_percentage ?? 0);
    const planned = Number(project.tasks_total_percentage ?? 0);
    return (
      <button
        type="button"
        className={`prj-plan ${compact ? 'prj-plan--compact' : ''} ${done >= 100 ? 'is-complete' : ''}`}
        onClick={() => openPlan(project)}
      >
        <span className="prj-plan-head">
          <PlanOutlined aria-hidden />
          <span className="prj-plan-label">{t('projects.plan.progress', { done })}</span>
          {!compact && (
            <span className="prj-plan-meta">
              {t('projects.plan.taskCount', { count })}
              {planned < 100 ? ` · ${t('projects.plan.plannedShort', { planned })}` : ''}
            </span>
          )}
        </span>
        <span className="prj-plan-bar" aria-hidden>
          <span style={{ width: `${Math.min(100, done)}%` }} />
        </span>
      </button>
    );
  };

  const renderTeam = (project: Project, size: number) => {
    const team = project.current_employees ?? [];
    return (
      <button type="button" className="prj-team" onClick={() => setTeamProjectId(project.id)}>
        {team.length > 0 && (
          <span className="prj-team-stack">
            {team.slice(0, 4).map((employee) => (
              <span className="prj-team-avatar" key={employee.id}>
                <PersonAvatar name={employee.name} surname={employee.surname} imageUrl={employee.imageUrl} size={size} />
              </span>
            ))}
            {team.length > 4 && (
              <span className="prj-team-more" style={{ width: size, height: size }}>
                +{team.length - 4}
              </span>
            )}
          </span>
        )}
        <span className="prj-team-label">
          {team.length === 0 ? t('projects.schedule.noTeam') : t('projects.teamCount', { count: team.length })}
        </span>
      </button>
    );
  };

  const renderMenu = (project: Project) => (
    <Dropdown menu={{ items: actionItems(project) }} trigger={['click']} placement="bottomRight">
      <Button type="text" size="small" icon={<MoreOutlined />} aria-label={t('projects.schedule.moreActions')} />
    </Dropdown>
  );

  const mapsUrl = (location: string) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;

  const pastEmployees = teamProject?.employees?.filter((employee) => employee.pivot.end_date) ?? [];

  return (
    <MainLayout bare>
      {modalContextHolder}
      <PageHeader
        title={t('projects.title')}
        subtitle={
          loading
            ? undefined
            : t('projects.summary', {
                active: statusCounts.active,
                finished: statusCounts.finished,
                canceled: statusCounts.canceled,
              })
        }
        actions={
          <>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: 'var(--e22-text-muted)' }} />}
              placeholder={t('projects.searchByName')}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              style={{ width: 260 }}
            />
            <Button icon={<ReloadOutlined spin={loading} />} onClick={fetchProjects} disabled={loading}>
              {t('dashboard.refresh')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm()}>
              {t('projects.newProject')}
            </Button>
          </>
        }
      />

      {!loading && <ProjectAlerts projects={projects} onSelect={focusProject} />}

      <div className="prj-toolbar">
        <FilterChips<StatusFilter>
          ariaLabel={t('projects.status')}
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { key: 'all', label: t('projects.allStatuses'), count: projects.length },
            ...(attentionCount > 0
              ? [{ key: 'attention' as StatusFilter, label: t('projects.schedule.needsAttention'), count: attentionCount }]
              : []),
            { key: 'active', label: t('projects.statuses.active'), count: statusCounts.active },
            { key: 'finished', label: t('projects.statuses.finished'), count: statusCounts.finished },
            { key: 'canceled', label: t('projects.statuses.canceled'), count: statusCounts.canceled },
          ]}
        />
        {hasFilters && (
          <Button size="small" icon={<CloseCircleOutlined />} onClick={clearFilters}>
            {t('projects.clearFilters')}
          </Button>
        )}
        <span className="prj-toolbar-spacer" />
        <Select<SortKey>
          value={sortKey}
          onChange={setSortKey}
          style={{ width: 190 }}
          aria-label={t('projects.schedule.sortBy')}
          options={[
            { value: 'schedule', label: t('projects.schedule.sortSchedule') },
            { value: 'newest', label: t('projects.schedule.sortNewest') },
            { value: 'name', label: t('projects.schedule.sortName') },
          ]}
        />
        <Segmented<ViewKey>
          value={view}
          onChange={changeView}
          options={[
            { value: 'grid', icon: <AppstoreOutlined />, title: t('projects.schedule.viewGrid') },
            { value: 'list', icon: <UnorderedListOutlined />, title: t('projects.schedule.viewList') },
          ]}
        />
      </div>

      {loading ? (
        <Row gutter={[20, 20]}>
          {[0, 1, 2].map((key) => (
            <Col key={key} xs={24} lg={12} xxl={8}>
              <div className="e22-card e22-section">
                <Skeleton active paragraph={{ rows: 4 }} />
              </div>
            </Col>
          ))}
        </Row>
      ) : visibleProjects.length === 0 ? (
        <div className="e22-card e22-section">
          <EmptyState
            icon={hasFilters ? <SearchOutlined /> : <ProjectOutlined />}
            text={hasFilters ? t('projects.noMatching') : t('projects.noProjects')}
            action={
              hasFilters ? (
                <button type="button" className="e22-link-button" onClick={clearFilters}>
                  {t('projects.clearFilters')}
                </button>
              ) : (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm()}>
                  {t('projects.newProject')}
                </Button>
              )
            }
          />
        </div>
      ) : view === 'grid' ? (
        <div className="prj-grid">
          {visibleProjects.map((project) => {
            const status = (STATUS_ORDER.includes(project.status as StatusKey) ? project.status : 'active') as StatusKey;
            const schedule = scheduleOf(project);
            const files = project.files ?? [];
            return (
              <article
                key={project.id}
                id={`project-${project.id}`}
                className={`e22-card prj-card prj-card--${status} prj-card--${schedule.state} ${
                  highlightId === project.id ? 'is-highlighted' : ''
                }`}
              >
                <header className="prj-head">
                  <div className="prj-pills">
                    <Pill tone={STATUS_TONE[status]}>{t(`projects.statuses.${status}`)}</Pill>
                    {renderSchedulePill(schedule)}
                  </div>
                  {renderMenu(project)}
                </header>

                <button type="button" className="prj-name" onClick={() => openPlan(project)}>
                  {project.name}
                </button>
                <a className="prj-location" href={mapsUrl(project.location)} target="_blank" rel="noreferrer">
                  <EnvironmentOutlined aria-hidden /> <span>{project.location}</span>
                </a>
                {project.description ? <p className="prj-description">{project.description}</p> : null}

                {renderTimeline(project, schedule)}
                {renderPlan(project)}

                <footer className="prj-foot">
                  {renderTeam(project, 28)}
                  <div className="prj-quick">
                    <Tooltip title={t('projects.files.title')}>
                      <Button size="small" type="text" icon={<FolderOpenOutlined />} onClick={() => openFiles(project)}>
                        {files.length}
                      </Button>
                    </Tooltip>
                    <Tooltip title={t('projects.photos.button')}>
                      <Button
                        size="small"
                        type="text"
                        icon={<CameraOutlined />}
                        onClick={() => setPhotosProjectId(project.id)}
                        aria-label={t('projects.photos.button')}
                      />
                    </Tooltip>
                    <Tooltip title={t('projects.attendance.title')}>
                      <Button
                        size="small"
                        type="text"
                        icon={<ClockCircleOutlined />}
                        onClick={() => openAttendance(project)}
                        aria-label={t('projects.attendance.title')}
                      />
                    </Tooltip>
                  </div>
                </footer>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="e22-card prj-list">
          <div className="prj-list-head" aria-hidden>
            <span>{t('projects.name')}</span>
            <span>{t('projects.schedule.timeline')}</span>
            <span>{t('projects.schedule.team')}</span>
            <span>{t('projects.status')}</span>
            <span />
          </div>
          {visibleProjects.map((project) => {
            const status = (STATUS_ORDER.includes(project.status as StatusKey) ? project.status : 'active') as StatusKey;
            const schedule = scheduleOf(project);
            return (
              <div
                key={project.id}
                id={`project-${project.id}`}
                className={`prj-list-row prj-list-row--${status} ${highlightId === project.id ? 'is-highlighted' : ''}`}
              >
                <div className="prj-list-main">
                  <button type="button" className="prj-list-name" onClick={() => openPlan(project)}>
                    {project.name}
                  </button>
                  <a className="prj-location" href={mapsUrl(project.location)} target="_blank" rel="noreferrer">
                    <EnvironmentOutlined aria-hidden /> <span>{project.location}</span>
                  </a>
                  {renderPlan(project, true)}
                </div>
                <div className="prj-list-timeline">{renderTimeline(project, schedule, true)}</div>
                <div className="prj-list-team">{renderTeam(project, 26)}</div>
                <div className="prj-pills">
                  <Pill tone={STATUS_TONE[status]}>{t(`projects.statuses.${status}`)}</Pill>
                  {renderSchedulePill(schedule)}
                </div>
                <div className="prj-list-menu">{renderMenu(project)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / edit */}
      <Drawer
        title={editingProject ? t('projects.editProject') : t('projects.newProject')}
        placement="right"
        onClose={() => setFormVisible(false)}
        open={formVisible}
        width={500}
        // Above the plan drawer, which opens it with "Edit project"
        zIndex={1010}
        footer={
          <div className="prj-drawer-footer">
            <Button onClick={() => setFormVisible(false)}>{t('common.cancel')}</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              {editingProject ? t('common.update') : t('common.create')}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label={t('projects.name')} rules={[{ required: true, message: t('projects.nameRequired') }]}>
            <Input autoFocus />
          </Form.Item>
          <Form.Item
            name="location"
            label={t('projects.location')}
            rules={[{ required: true, message: t('projects.locationRequired') }]}
          >
            <Input prefix={<EnvironmentOutlined style={{ color: 'var(--e22-text-muted)' }} />} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="start_date" label={t('projects.startDate')}>
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} placeholder={t('projects.schedule.pickDate')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="end_date"
                label={t('projects.endDate')}
                dependencies={['start_date']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: (_, value?: Dayjs | null) => {
                      const start: Dayjs | null = getFieldValue('start_date');
                      return !value || !start || !value.isBefore(start, 'day')
                        ? Promise.resolve()
                        : Promise.reject(new Error(t('projects.schedule.endBeforeStart')));
                    },
                  }),
                ]}
              >
                <DatePicker
                  format="DD.MM.YYYY"
                  style={{ width: '100%' }}
                  placeholder={t('projects.schedule.pickDate')}
                  disabledDate={(current) => {
                    const start: Dayjs | null = form.getFieldValue('start_date');
                    return !!start && current.isBefore(start, 'day');
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <div className="prj-form-hint">
            <InfoCircleOutlined aria-hidden />
            <span>{t('projects.schedule.formHint', { days: SOON_DAYS })}</span>
          </div>
          <Form.Item name="description" label={t('projects.description')}>
            <TextArea rows={4} />
          </Form.Item>
          {editingProject && (
            <Form.Item
              name="status"
              label={t('projects.status')}
              rules={[{ required: true, message: t('projects.statusRequired') }]}
            >
              <Select
                options={STATUS_ORDER.map((status) => ({ value: status, label: t(`projects.statuses.${status}`) }))}
              />
            </Form.Item>
          )}
        </Form>
      </Drawer>

      {/* Team */}
      <Drawer
        title={t('projects.employees.title')}
        placement="right"
        onClose={() => setTeamProjectId(null)}
        open={!!teamProject}
        width={560}
      >
        {teamProject && (
          <>
            <div className="prj-drawer-subject">
              <ProjectOutlined />
              <div className="e22-row-body">
                <div className="e22-row-title">{teamProject.name}</div>
                <div className="e22-row-subtitle">{teamProject.location}</div>
              </div>
            </div>

            <div className="prj-assign">
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={t('projects.employees.selectEmployee')}
                value={assigningEmployeeId}
                onChange={setAssigningEmployeeId}
                style={{ flex: 1 }}
                options={allEmployees
                  .filter((employee) => !teamProject.current_employees.some((current) => current.id === employee.id))
                  .map((employee) => ({ value: employee.id!, label: `${employee.name} ${employee.surname}` }))}
              />
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                loading={assigning}
                disabled={!assigningEmployeeId}
                onClick={() => handleAssignEmployee()}
              >
                {t('projects.employees.assignEmployee')}
              </Button>
            </div>

            <div className="prj-drawer-section">{t('projects.employees.activeEmployees')}</div>
            {teamProject.current_employees.length === 0 ? (
              <EmptyState icon={<TeamOutlined />} text={t('projects.employees.noActiveEmployees')} />
            ) : (
              teamProject.current_employees.map((employee: ProjectEmployee) => (
                <div className="e22-row" key={employee.id}>
                  <PersonAvatar name={employee.name} surname={employee.surname} imageUrl={employee.imageUrl} size={38} />
                  <div className="e22-row-body">
                    <div className="e22-row-title">
                      {employee.name} {employee.surname}
                    </div>
                    <div className="e22-row-subtitle">
                      {employee.email}
                      {employee.pivot.role ? ` · ${employee.pivot.role}` : ''}
                    </div>
                    <div className="e22-row-subtitle">
                      {t('projects.employees.assignedDate')}: {formatDate(employee.pivot.assigned_date)}
                    </div>
                  </div>
                  <Popconfirm
                    title={t('projects.employees.removeEmployee')}
                    description={`${employee.name} ${employee.surname}`}
                    onConfirm={() => handleRemoveEmployee(employee.id)}
                    okText={t('common.yes')}
                    cancelText={t('common.no')}
                    okButtonProps={{ danger: true }}
                  >
                    <Tooltip title={t('projects.employees.removeEmployee')}>
                      <Button type="text" danger icon={<UserDeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                </div>
              ))
            )}

            <div className="prj-drawer-section">{t('projects.employees.employeeHistory')}</div>
            {pastEmployees.length === 0 ? (
              <EmptyState icon={<TeamOutlined />} text={t('projects.employees.noEmployeeHistory')} />
            ) : (
              pastEmployees.map((employee: ProjectEmployee) => (
                <div className="e22-row" key={`${employee.id}-${employee.pivot.end_date}`}>
                  <PersonAvatar
                    name={employee.name}
                    surname={employee.surname}
                    imageUrl={employee.imageUrl}
                    size={38}
                    muted
                  />
                  <div className="e22-row-body">
                    <div className="e22-row-title">
                      {employee.name} {employee.surname}
                    </div>
                    <div className="e22-row-subtitle">
                      {formatDate(employee.pivot.assigned_date)} – {formatDate(employee.pivot.end_date!)}
                    </div>
                  </div>
                  <Tooltip title={t('projects.employees.assignEmployee')}>
                    <Button
                      type="text"
                      icon={<UserAddOutlined />}
                      loading={assigning}
                      onClick={() => handleAssignEmployee(employee.id)}
                    />
                  </Tooltip>
                </div>
              ))
            )}
          </>
        )}
      </Drawer>

      {/* Files */}
      <Drawer
        title={t('projects.files.title')}
        placement="right"
        onClose={() => setFilesProjectId(null)}
        open={!!filesProject}
        width={560}
      >
        {filesProject && (
          <>
            <div className="prj-drawer-subject">
              <ProjectOutlined />
              <div className="e22-row-body">
                <div className="e22-row-title">{filesProject.name}</div>
                <div className="e22-row-subtitle">{filesProject.location}</div>
              </div>
            </div>

            <div className="prj-upload">
              {selectedFile ? (
                <>
                  <div className="prj-picked">
                    <span
                      className="emp-file-icon"
                      style={{ background: fileKind(selectedFile.name).soft, color: fileKind(selectedFile.name).color }}
                    >
                      {fileKind(selectedFile.name).icon}
                    </span>
                    <div className="e22-row-body">
                      <div className="e22-row-title">{selectedFile.name}</div>
                      <div className="e22-row-subtitle">
                        {fileKind(selectedFile.name).label} · {Math.max(1, Math.round(selectedFile.size / 1024))} KB
                      </div>
                    </div>
                    <Button
                      type="text"
                      icon={<CloseCircleOutlined />}
                      disabled={uploadingFile}
                      onClick={() => {
                        setSelectedFile(null);
                        setFileName('');
                      }}
                    />
                  </div>
                  <Input
                    placeholder={t('projects.files.fileName')}
                    value={fileName}
                    onChange={(event) => setFileName(event.target.value)}
                    maxLength={255}
                    disabled={uploadingFile}
                    onPressEnter={handleFileUpload}
                  />
                  <Button type="primary" block loading={uploadingFile} disabled={!fileName.trim()} onClick={handleFileUpload}>
                    {t('projects.files.uploadFile')}
                  </Button>
                </>
              ) : (
                <Upload.Dragger
                  multiple={false}
                  showUploadList={false}
                  beforeUpload={(file) => {
                    setSelectedFile(file);
                    setFileName(file.name.replace(/\.[^.]+$/, '').slice(0, 255));
                    return false;
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">{t('projects.files.chooseFile')}</p>
                  <p className="ant-upload-hint">{t('projects.files.typesHint')}</p>
                </Upload.Dragger>
              )}
            </div>

            <div className="prj-drawer-section">
              {t('projects.files.title')} · {t('files.count', { count: filesProject.files?.length ?? 0 })}
            </div>

            {!filesProject.files || filesProject.files.length === 0 ? (
              <EmptyState icon={<FolderOpenOutlined />} text={t('projects.files.noFiles')} />
            ) : (
              filesProject.files.map((file: ProjectFile) => {
                const kind = fileKind(file.file_path || file.file_url);
                return (
                  <div className="e22-row" key={file.id}>
                    <span className="emp-file-icon" style={{ background: kind.soft, color: kind.color }}>
                      {kind.icon}
                    </span>
                    <div className="e22-row-body">
                      <div className="e22-row-title">{file.file_name}</div>
                      <div className="e22-row-subtitle">
                        {kind.label} · {t('projects.files.uploaded')}: {formatDate(file.created_at)}
                      </div>
                    </div>
                    <Tooltip title={t('projects.files.viewFile')}>
                      <Button type="text" icon={<ExportOutlined />} href={file.file_url} target="_blank" rel="noreferrer" />
                    </Tooltip>
                    <Popconfirm
                      title={t('projects.files.deleteConfirm')}
                      description={file.file_name}
                      onConfirm={() => handleDeleteFile(file.id)}
                      okText={t('common.yes')}
                      cancelText={t('common.no')}
                      okButtonProps={{ danger: true }}
                    >
                      <Tooltip title={t('projects.files.deleteFile')}>
                        <Button type="text" danger icon={<DeleteOutlined />} loading={deletingFileId === file.id} />
                      </Tooltip>
                    </Popconfirm>
                  </div>
                );
              })
            )}

            <div className="emp-doc-note">
              <InfoCircleOutlined />
              <span>{t('projects.files.visibleHint')}</span>
            </div>
          </>
        )}
      </Drawer>

      {/* Attendance */}
      <Drawer
        title={t('projects.attendance.title')}
        placement="right"
        onClose={() => setAttendanceProject(null)}
        open={!!attendanceProject}
        width={620}
      >
        {attendanceProject && (
          <>
            <div className="prj-drawer-subject">
              <ProjectOutlined />
              <div className="e22-row-body">
                <div className="e22-row-title">{attendanceProject.name}</div>
                <div className="e22-row-subtitle">{attendanceProject.location}</div>
              </div>
            </div>

            {attendanceLoading ? (
              <Skeleton active paragraph={{ rows: 5 }} />
            ) : !attendanceData || attendanceData.data.length === 0 ? (
              <EmptyState icon={<ClockCircleOutlined />} text={t('projects.attendance.noAttendanceRecords')} />
            ) : (
              <>
                <div className="prj-total">
                  <span className="prj-total-label">{t('projects.attendance.totalDuration')}</span>
                  <span className="prj-total-value">{formatDuration(attendanceData.total_duration_seconds)}</span>
                  <span className="prj-total-sub">
                    {t('projects.attendance.recordCount', { count: attendanceData.data.length })}
                  </span>
                </div>

                <div className="prj-drawer-section">{t('projects.attendance.perEmployee')}</div>
                {attendanceByEmployee.map((entry) => {
                  const [first, ...rest] = entry.name.split(' ');
                  return (
                    <div className="e22-row" key={entry.name}>
                      <PersonAvatar name={first} surname={rest.join(' ')} size={34} />
                      <div className="e22-row-body">
                        <div className="e22-row-title">{entry.name}</div>
                        <div className="e22-row-subtitle">
                          {t('projects.attendance.recordCount', { count: entry.records })}
                        </div>
                      </div>
                      <Pill tone="primary">{formatDuration(entry.seconds)}</Pill>
                    </div>
                  );
                })}

                <div className="prj-drawer-section">{t('projects.attendance.attendanceRecords')}</div>
                {attendanceData.data.map((record: Attendance) => (
                  <div className="e22-row" key={record.id}>
                    <span className="prj-record-icon">
                      <ClockCircleOutlined />
                    </span>
                    <div className="e22-row-body">
                      <div className="e22-row-title">
                        {record.employee?.name} {record.employee?.surname}
                      </div>
                      <div className="e22-row-subtitle">
                        {dayjs(record.check_in).format('DD MMM YYYY')} · {dayjs(record.check_in).format('HH:mm')}
                        {record.check_out ? ` – ${dayjs(record.check_out).format('HH:mm')}` : ''}
                      </div>
                    </div>
                    {record.check_out ? (
                      <Pill>{formatDuration(record.duration_seconds ?? 0)}</Pill>
                    ) : (
                      <Pill tone="success" live>
                        {t('dashboard.filterWorking')}
                      </Pill>
                    )}
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </Drawer>

      {/* Photos employees posted (feeds) */}
      <ProjectPhotosDrawer
        project={projects.find((project) => project.id === photosProjectId) ?? null}
        onClose={() => setPhotosProjectId(null)}
      />

      {/* Plan: the project's tasks on a Ready / In progress / Done board (opens on project click) */}
      <ProjectPlanDrawer
        project={planProject}
        onClose={closePlan}
        onTasksChange={handleTasksChange}
        onEditProject={openForm}
        onShare={(project) => setShareProjectId(project.id)}
      />

      {/* Client links: read-only /p/<token> pages, what they show is chosen per link */}
      <ProjectShareDrawer
        project={projects.find((project) => project.id === shareProjectId) ?? null}
        onClose={() => setShareProjectId(null)}
      />
    </MainLayout>
  );
};

export default Projects;
